/* eslint-disable import/no-named-as-default */
/**
 * ConnectionCanvas - SVG overlay for rendering connection lines
 *
 * Features:
 * - Absolute positioned SVG covering the entire linking area
 * - Animated bezier curves with framer-motion
 * - Suggested link badges with approve/reject buttons
 * - Sync suggestion badges for linked entities
 * - Drag preview line
 */

import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';

import type { FilevineEntitySyncSuggestionSetSchema } from '@/services/api/generated/entitySyncService';

import {
  getSuggestionsForEntityPair,
  type SyncSuggestionsByEntity,
} from '../sync-suggestions/useSyncSuggestionsMap';
import type {
  ConnectionLineData,
  DragState,
  ResolvedLink,
} from '../types/linking-view.types';
import ApprovedLinkBadge from './ApprovedLinkBadge';
import ConnectionLine from './ConnectionLine';
import DragPreviewLine from './DragPreviewLine';
import SuggestedLinkBadge from './SuggestedLinkBadge';

interface ConnectionCanvasProps {
  connections: ConnectionLineData[];
  dragState: DragState;
  onApprove: (linkId: string) => Promise<void>;
  onReject: (linkId: string) => Promise<void>;
  onUnlink: (linkId: string) => Promise<void>;
  suggestedLinks: ResolvedLink[];
  approvedLinks: ResolvedLink[];
  getDropTargetPosition?: (entityId: string) => { x: number; y: number } | null;
  /** Sync suggestions map for showing sync badges on approved links */
  syncSuggestionsMap?: SyncSuggestionsByEntity;
  /** Callback when sync suggestion badge is clicked */
  onSyncSuggestionClick?: (
    suggestions: FilevineEntitySyncSuggestionSetSchema[],
  ) => void;
}

/**
 * Calculate the midpoint of a bezier curve for badge placement
 * Uses simple linear midpoint since the curve is mostly horizontal
 */
function getBezierMidpoint(
  start: { x: number; y: number },
  end: { x: number; y: number },
): { x: number; y: number } {
  // Simple midpoint - works well for horizontal curves
  return {
    x: (start.x + end.x) / 2,
    y: (start.y + end.y) / 2,
  };
}

export const ConnectionCanvas: React.FC<ConnectionCanvasProps> = ({
  connections,
  dragState,
  onApprove,
  onReject,
  onUnlink,
  suggestedLinks,
  approvedLinks,
  getDropTargetPosition,
  syncSuggestionsMap,
  onSyncSuggestionClick,
}) => {
  // The SVG is positioned inside data-linking-container, so no ref needed
  // It uses absolute positioning to overlay the grid

  // Create maps for quick lookup
  const suggestedLinkMap = new Map(suggestedLinks.map((l) => [l.id, l]));
  const approvedLinkMap = new Map(approvedLinks.map((l) => [l.id, l]));

  // Determine the end position for drag preview line
  // If there's a valid drop target, snap to its position
  let dragEndPosition = dragState.currentPosition;
  if (dragState.validDropTarget && getDropTargetPosition) {
    const targetPos = getDropTargetPosition(dragState.validDropTarget);
    if (targetPos) {
      dragEndPosition = targetPos;
    }
  }

  return (
    <>
      {/* SVG layer for lines - positioned absolutely within data-linking-container */}
      <svg
        className="pointer-events-none absolute inset-0 z-10 h-full w-full"
        style={{ overflow: 'visible' }}
      >
        <defs>
          {/* Animated dash pattern for suggested links */}
          <pattern
            id="marchingAnts"
            patternUnits="userSpaceOnUse"
            width="12"
            height="1"
          >
            <motion.rect
              x="0"
              y="0"
              width="6"
              height="1"
              fill="currentColor"
              animate={{ x: [0, 12] }}
              transition={{
                duration: 0.5,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
          </pattern>
        </defs>

        <AnimatePresence>
          {connections.map((connection) => (
            <ConnectionLine key={connection.id} data={connection} />
          ))}
        </AnimatePresence>

        {/* Drag preview line */}
        {dragState.isDragging && dragState.source && dragEndPosition && (
          <DragPreviewLine
            start={dragState.source.position}
            end={dragEndPosition}
            isSnapped={!!dragState.validDropTarget}
          />
        )}
      </svg>

      {/* Badge layer for suggested links (outside SVG for better interactivity) */}
      <AnimatePresence>
        {connections
          .filter((c) => c.status === 'suggested')
          .map((connection) => {
            const suggestedLink = suggestedLinkMap.get(connection.linkId);
            if (!suggestedLink) return null;

            const midpoint = getBezierMidpoint(
              connection.start,
              connection.end,
            );

            return (
              <SuggestedLinkBadge
                key={`badge-${connection.linkId}`}
                linkId={connection.linkId}
                confidence={suggestedLink.confidence}
                position={midpoint}
                onApprove={() => onApprove(connection.linkId)}
                onReject={() => onReject(connection.linkId)}
              />
            );
          })}
      </AnimatePresence>

      {/* Badge layer for approved links (outside SVG for better interactivity) */}
      <AnimatePresence>
        {connections
          .filter((c) => c.status === 'approved')
          .map((connection) => {
            const approvedLink = approvedLinkMap.get(connection.linkId);
            if (!approvedLink) return null;

            const midpoint = getBezierMidpoint(
              connection.start,
              connection.end,
            );

            // Get sync suggestions for this link
            const syncSuggestions = syncSuggestionsMap
              ? getSuggestionsForEntityPair(
                  syncSuggestionsMap,
                  approvedLink.finchEntity.id,
                  approvedLink.filevineEntity.id,
                  connection.linkId,
                )
              : [];

            return (
              <React.Fragment key={`badges-${connection.linkId}`}>
                <ApprovedLinkBadge
                  linkId={connection.linkId}
                  position={midpoint}
                  onUnlink={() => onUnlink(connection.linkId)}
                />
                {/* Sync suggestion badge - positioned below the approved badge */}
                {/* {syncSuggestions.length > 0 && onSyncSuggestionClick && (
                  <SyncSuggestionBadge
                    suggestionSets={syncSuggestions}
                    position={midpoint}
                    onClick={() => onSyncSuggestionClick(syncSuggestions)}
                  />
                )} */}
              </React.Fragment>
            );
          })}
      </AnimatePresence>
    </>
  );
};

export default ConnectionCanvas;
